/**
 * 어시스트가 빈 수납건의 팀 귀속 정본 (2026-09-03 설계 합의)
 *
 * 어시스트 칸이 비어 있거나(엑셀에서 '미지정'으로 채워짐), 어시스트 이름이 급여 명단에 없고
 * 엑셀 팀칸도 비어 있는 수납건은 어느 팀 실적인지 정할 수 없다. 이 함수가 그 수납건("풀")을
 * 아래 순서로 귀속한다. **더 구체적인 지정이 이긴다.**
 *
 *   ① 수납건 수동 지정  — 그 건만 (관리자가 데일리 리포트에서 차트번호 단위로 지정)
 *                        같은 날 같은 차트번호가 여러 건이면 그 묶음 전체가 함께 움직인다(키가 날짜|차트).
 *                        화면도 같은 묶음을 한 줄로 보여준다 — 지정 단위와 표시 단위를 일치시킨다.
 *   ② 의사 월 매핑     — 그 의사의 그 달 풀 전부를 한 팀으로 강제 (그 달만, 상속 없음)
 *   ③ 그날 비율        — 그날 그 진료의사의 '어시스트 있는 수납건' 팀별 건수 비율로 배분
 *   ④ 그 달 비율       — 그 달 그 의사의 같은 비율
 *   ⑤ 미지정           — 위 넷이 전부 불가 (그 달 내내 어시스트 없는 진료만 한 의사, 의사 이름 없음)
 *
 * 풀의 정의는 집계행(teamCollection)과 같아야 한다: 그 날짜의 어시스트 행이 급여 명단으로도 엑셀·업로드
 * 팀칸으로도 팀을 못 정할 때만 풀이다. 빈 어시스트 행에 팀이 이미 박혀 있으면(업로드 시점 매핑) 풀이 아니다.
 *
 * 이 파일은 firebase·React를 모른다. 입력은 그 달 수납건 목록과 지정값, 출력은 팀별 금액과 근거다.
 * 검증 불변식: manualTotal + autoTotal + unassignedTotal === poolTotal (원 단위, 반올림 잔차 없음).
 */

export interface ReceiptRow {
  /** YYYY-MM-DD */
  date: string;
  chartNumber: string;
  assistName: string;
  doctorName: string;
  amount: number;
}

export type AttributionMethod = 'manual' | 'doctorMapping' | 'dayRatio' | 'monthRatio';

export interface AttributedShare {
  date: string;
  doctorName: string;
  team: string;
  amount: number;
  method: AttributionMethod;
  /** 수동 지정일 때만 */
  chartNumber?: string;
}

export interface UnassignedShare {
  date: string;
  doctorName: string;
  amount: number;
  count: number;
  reason: '의사 없음' | '그 달 어시스트 있는 진료 없음';
}

export interface AttributionResult {
  /** 배분 대상 총액 (풀에 들어온 수납건 금액 합) */
  poolTotal: number;
  poolCount: number;
  /** 팀별 합 — 수동(①) */
  manualByTeam: Record<string, number>;
  /** 팀별 합 — 자동(②③④), 의사별로 나눠서 */
  autoByTeam: Record<string, Record<string, number>>;
  manualTotal: number;
  autoTotal: number;
  unassignedTotal: number;
  shares: AttributedShare[];
  unassigned: UnassignedShare[];
  /** 집계행이 없어 풀에 넣지 않은 수납건 (날짜·이름별 합) — 데이터 불일치 신호 */
  orphans: { date: string; assistName: string; amount: number; count: number }[];
}

export interface AttributionInput {
  receipts: readonly ReceiptRow[];
  /**
   * 어시스트 이름을 팀으로 푼다 (급여 명단 → 엑셀 팀칸).
   *   팀 문자열   → 직접 귀속 (풀 아님)
   *   null/'미지정' → 그 날짜에 집계행이 있고 미지정으로 떨어짐 (풀)
   *   undefined   → 그 날짜에 그 이름의 집계행이 없음 → 풀에 넣지 않고 orphan 으로 보고
   *                 (수납건만 있고 집계행이 없는 돈을 지어내지 않는다 — Codex 재심 P0-1)
   * 날짜를 받는 이유: 엑셀 팀칸은 날짜별 행에 있다.
   */
  resolveAssistTeam: (assistName: string, date: string) => string | null | undefined;
  /** `${date}|${chartNumber}` → 팀 */
  receiptOverrides?: Record<string, string>;
  /** 의사 → 팀 (그 달) */
  doctorTeamMapping?: Record<string, string>;
}

const isBlankAssist = (name: string | null | undefined): boolean => {
  const n = (name ?? '').toString().trim();
  return n === '' || n === '미지정';
};

const toAmount = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n) : 0;
};

/** 지정 키 = 날짜|차트번호. 같은 날 같은 차트의 여러 건은 한 묶음으로 함께 지정된다. */
const overrideKey = (date: string, chartNumber: string) => `${date}|${chartNumber}`;

/**
 * 정수 금액을 가중치 비율로 나누되 합이 정확히 amount가 되게 한다 (최대 잔여 방식).
 * 가중치가 전부 0이면 빈 객체.
 */
export function splitByWeights(amount: number, weights: Record<string, number>): Record<string, number> {
  const entries = Object.entries(weights).filter(([, w]) => w > 0);
  const totalW = entries.reduce((s, [, w]) => s + w, 0);
  if (entries.length === 0 || totalW <= 0) return {};
  if (entries.length === 1) return { [entries[0][0]]: amount };

  const raw = entries.map(([team, w]) => ({ team, exact: (amount * w) / totalW }));
  const floored = raw.map((r) => ({ ...r, value: Math.trunc(r.exact) }));
  let remainder = amount - floored.reduce((s, r) => s + r.value, 0);
  // 소수부가 큰 순서로 1원씩 배분 (음수 금액이면 -1원씩)
  const step = remainder >= 0 ? 1 : -1;
  const order = [...floored].sort((a, b) => Math.abs(b.exact - b.value) - Math.abs(a.exact - a.value));
  let i = 0;
  while (remainder !== 0) {
    order[i % order.length].value += step;
    remainder -= step;
    i++;
  }
  const out: Record<string, number> = {};
  floored.forEach((r) => { out[r.team] = r.value; });
  return out;
}

/**
 * 풀 귀속 계산. 순수 함수.
 */
export function attributeBlankAssistReceipts(input: AttributionInput): AttributionResult {
  const overrides = input.receiptOverrides || {};
  const doctorMapping = input.doctorTeamMapping || {};
  // 빈 어시스트도 '미지정'이라는 이름으로 해석기를 통과시킨다.
  // 업로드 파이프라인이 그 날짜의 빈 행에 팀을 이미 박아 넣었으면(6·7월: 의사 매핑으로 team='1팀')
  // 집계행이 그 돈을 그 팀에 직접 귀속하므로, 여기서 다시 배분하면 이중 계상이 된다.
  // 풀 = "집계행이 미지정으로 떨어지는 수납건" 과 정확히 같은 집합이어야 한다.
  const resolve = (name: string, date: string): string | null | undefined => {
    const key = isBlankAssist(name) ? '미지정' : name;
    const t = input.resolveAssistTeam(key, date);
    if (t === undefined) return undefined;
    return t && t !== '미지정' ? t : null;
  };

  // ── 1) 풀과 근거 집합 분리 ──
  const pool: ReceiptRow[] = [];
  /** 날짜 → 의사 → 팀 → 어시스트 있는 수납건 건수 */
  const dayWeights: Record<string, Record<string, Record<string, number>>> = {};
  /** 의사 → 팀 → 건수 (그 달) */
  const monthWeights: Record<string, Record<string, number>> = {};

  const orphanMap: Record<string, { date: string; assistName: string; amount: number; count: number }> = {};
  /** 근거 중복 제거: 같은 날 같은 차트번호는 한 건 — 중복 행으로 비율을 부풀리지 못하게 (Codex 재심 P1-5) */
  const evidenceSeen = new Set<string>();

  input.receipts.forEach((raw) => {
    const r: ReceiptRow = {
      date: raw.date,
      chartNumber: String(raw.chartNumber ?? ''),
      assistName: (raw.assistName ?? '').toString().trim(),
      doctorName: (raw.doctorName ?? '').toString().trim(),
      amount: toAmount(raw.amount),
    };
    const team = resolve(r.assistName, r.date);
    if (team === undefined) {
      // 집계행이 없는 수납건 — 돈을 지어내지 않고 보고만 한다
      if (r.amount === 0 && !r.doctorName) return;
      const k = `${r.date}|${r.assistName || '미지정'}`;
      const o = (orphanMap[k] = orphanMap[k] || { date: r.date, assistName: r.assistName || '미지정', amount: 0, count: 0 });
      o.amount += r.amount; o.count += 1;
      return;
    }
    if (team === null) {
      // 엑셀의 빈 자리표시 행(어시스트·의사 없음, 0원)은 수납건이 아니다 — 풀에 세지 않는다
      if (r.amount === 0 && !r.doctorName) return;
      pool.push(r);
      return;
    }
    // 근거(어시스트 있는 수납건)의 자격: 의사가 있고, 실수납 금액이 양수이며, 같은 날 같은 차트는 한 번만
    if (!r.doctorName) return;
    if (r.amount <= 0) return;
    const ek = `${r.date}|${r.doctorName}|${r.chartNumber}`;
    if (r.chartNumber && evidenceSeen.has(ek)) return;
    evidenceSeen.add(ek);
    const d = (dayWeights[r.date] = dayWeights[r.date] || {});
    const dd = (d[r.doctorName] = d[r.doctorName] || {});
    dd[team] = (dd[team] || 0) + 1;
    const m = (monthWeights[r.doctorName] = monthWeights[r.doctorName] || {});
    m[team] = (m[team] || 0) + 1;
  });

  // ── 2) 귀속 ──
  const shares: AttributedShare[] = [];
  const unassignedMap: Record<string, UnassignedShare> = {};
  const manualByTeam: Record<string, number> = {};
  const autoByTeam: Record<string, Record<string, number>> = {};
  const addAuto = (team: string, doctor: string, amount: number) => {
    const t = (autoByTeam[team] = autoByTeam[team] || {});
    t[doctor] = (t[doctor] || 0) + amount;
  };
  const addUnassigned = (r: ReceiptRow, reason: UnassignedShare['reason']) => {
    const key = `${r.date}|${r.doctorName}|${reason}`;
    const u = (unassignedMap[key] = unassignedMap[key] || { date: r.date, doctorName: r.doctorName, amount: 0, count: 0, reason });
    u.amount += r.amount;
    u.count += 1;
  };

  pool.forEach((r) => {
    // ① 수납건 지정
    const manual = overrides[overrideKey(r.date, r.chartNumber)];
    if (manual && manual !== '미지정') {
      manualByTeam[manual] = (manualByTeam[manual] || 0) + r.amount;
      shares.push({ date: r.date, doctorName: r.doctorName, team: manual, amount: r.amount, method: 'manual', chartNumber: r.chartNumber });
      return;
    }
    if (!r.doctorName) {
      addUnassigned(r, '의사 없음');
      return;
    }
    // ② 의사 월 매핑 — 그 의사 풀 전부
    const forced = doctorMapping[r.doctorName];
    if (forced && forced !== '미지정') {
      addAuto(forced, r.doctorName, r.amount);
      shares.push({ date: r.date, doctorName: r.doctorName, team: forced, amount: r.amount, method: 'doctorMapping' });
      return;
    }
    // ③ 그날 비율 → ④ 그 달 비율
    const dayW = dayWeights[r.date]?.[r.doctorName] || {};
    let split = splitByWeights(r.amount, dayW);
    let method: AttributionMethod = 'dayRatio';
    if (Object.keys(split).length === 0) {
      split = splitByWeights(r.amount, monthWeights[r.doctorName] || {});
      method = 'monthRatio';
    }
    if (Object.keys(split).length === 0) {
      addUnassigned(r, '그 달 어시스트 있는 진료 없음');
      return;
    }
    Object.entries(split).forEach(([team, amount]) => {
      if (amount === 0 && r.amount !== 0) return;
      addAuto(team, r.doctorName, amount);
      shares.push({ date: r.date, doctorName: r.doctorName, team, amount, method });
    });
  });

  const unassigned = Object.values(unassignedMap).sort((a, b) => a.date.localeCompare(b.date));
  const manualTotal = Object.values(manualByTeam).reduce((s, v) => s + v, 0);
  const autoTotal = Object.values(autoByTeam).reduce((s, byDoc) => s + Object.values(byDoc).reduce((x, v) => x + v, 0), 0);
  const unassignedTotal = unassigned.reduce((s, u) => s + u.amount, 0);
  const poolTotal = pool.reduce((s, r) => s + r.amount, 0);

  return {
    poolTotal,
    poolCount: pool.length,
    manualByTeam,
    autoByTeam,
    manualTotal,
    autoTotal,
    unassignedTotal,
    shares,
    unassigned,
    orphans: Object.values(orphanMap).sort((a, b) => a.date.localeCompare(b.date)),
  };
}

/** 저장 문서(salesAttribution/{yearMonth})에서 지정값을 평탄화한다 */
export interface SalesAttributionDoc {
  receiptOverrides?: Record<string, { team: string; by?: string; at?: string }>;
  doctorTeamMapping?: Record<string, { team: string; by?: string; at?: string }>;
  pendingRefresh?: boolean;
  updatedAt?: string;
}

export function flattenAttributionDoc(doc: SalesAttributionDoc | null | undefined): {
  receiptOverrides: Record<string, string>;
  doctorTeamMapping: Record<string, string>;
} {
  const receiptOverrides: Record<string, string> = {};
  const doctorTeamMapping: Record<string, string> = {};
  Object.entries(doc?.receiptOverrides || {}).forEach(([k, v]) => { if (v?.team) receiptOverrides[k] = v.team; });
  Object.entries(doc?.doctorTeamMapping || {}).forEach(([k, v]) => { if (v?.team) doctorTeamMapping[k] = v.team; });
  return { receiptOverrides, doctorTeamMapping };
}

/** 수납건 지정 키 정본 — 저장·화면·계산이 이 함수 하나만 쓴다 (형식이 어긋나면 지정이 조용히 무시된다) */
export const attributionOverrideKey = overrideKey;
export const isBlankAssistName = isBlankAssist;

/**
 * 집계행(teamCollection)에서 날짜별 어시스트 해석 맵을 만든다 — 서비스·화면·검증 스크립트가 공유하는 규칙.
 *
 * · 이름 키는 compositeKey 앞부분과 raw assistName **둘 다** 등록한다. 수납건은 raw assistName으로
 *   찾아오므로 한쪽만 등록하면 풀 판정이 어긋나 이중 계상된다 (Codex 재심 P0-1).
 * · 같은 날 같은 이름의 행이 둘이면(자리표시) 팀이 풀린 값을 우선한다.
 * · 이 맵에 없는 (날짜, 이름)은 undefined → 풀이 아니라 orphan.
 */
export interface AssistRowLike {
  docDate?: string;
  date?: string;
  assistName?: string;
  compositeKey?: string;
  team?: string;
  isAssistRow?: boolean;
  isFinancialTransaction?: boolean;
}

export function buildDayAssistResolver(
  rows: readonly AssistRowLike[],
  resolveRow: (row: AssistRowLike, nameFromKey: string) => string
): (assistName: string, date: string) => string | undefined {
  const map: Record<string, Record<string, string>> = {};
  const put = (date: string, name: string, team: string) => {
    const byDay = (map[date] = map[date] || {});
    if (!byDay[name] || byDay[name] === '미지정') byDay[name] = team;
  };
  rows.forEach((row) => {
    const date = row.docDate || row.date;
    if (!row?.isAssistRow || !row.assistName || row.isFinancialTransaction || !date) return;
    const nameFromKey = row.compositeKey && row.compositeKey.includes('-') ? (row.compositeKey.split('-')[0] || row.assistName) : row.assistName;
    const team = resolveRow(row, nameFromKey);
    put(date, nameFromKey, team);
    if (row.assistName !== nameFromKey) put(date, row.assistName, team);
  });
  return (assistName, date) => {
    const key = isBlankAssist(assistName) ? '미지정' : assistName.trim();
    return map[date]?.[key];
  };
}
